import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { TracexLogo } from '@/components/icons/TracexLogo';
import { useTracexIntegrationService } from '@/services/integrations';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { type FC, useEffect, useRef, useState } from 'react';

const SAVE_DEBOUNCE_MS = 800;
const SAVED_INDICATOR_MS = 2500;

export const IntegrationsTab: FC = () => {
  const { integration, isPending, updateIntegration } =
    useTracexIntegrationService();
  const isEnabled = integration?.status === 'enabled';

  const [apiTokenInput, setApiTokenInput] = useState('');
  const [hasEdited, setHasEdited] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle'
  );
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync input with the stored token when the user isn't editing.
  useEffect(() => {
    if (!hasEdited) {
      setApiTokenInput(integration?.apiToken ?? '');
    }
  }, [integration?.apiToken, hasEdited]);

  // Debounced auto-save whenever the user edits the token.
  useEffect(() => {
    if (!hasEdited) return;
    const trimmed = apiTokenInput.trim();
    if (!trimmed) return;

    const handle = setTimeout(() => {
      setSaveStatus('saving');
      updateIntegration(
        { status: 'enabled', apiToken: trimmed },
        {
          onSuccess: () => {
            setSaveStatus('saved');
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(
              () => setSaveStatus('idle'),
              SAVED_INDICATOR_MS
            );
          },
          onError: () => setSaveStatus('idle'),
        }
      );
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenInput, hasEdited]);

  useEffect(
    () => () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    },
    []
  );

  const handleToggle = (checked: boolean) => {
    updateIntegration({ status: checked ? 'enabled' : 'disabled' });
    if (!checked) {
      setHasEdited(false);
      setSaveStatus('idle');
    }
  };

  if (isPending) {
    return (
      <FlexCol alignItems="center" justifyContent="center" className="py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </FlexCol>
    );
  }

  return (
    <FlexCol gap="6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Integrations</h2>
        <Text variant="body-sm" color="secondary" className="mt-1">
          Connect external services to your workspace.
        </Text>
      </div>

      <div className="rounded-lg border border-border">
        <FlexRow alignItems="center" justifyContent="between" className="p-4">
          <FlexRow alignItems="center" gap="3">
            <TracexLogo className="h-5 w-auto" />
          </FlexRow>
          <FlexRow alignItems="center" gap="2">
            <Label
              htmlFor="tracex-toggle"
              className="text-sm text-muted-foreground"
            >
              {isEnabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch
              id="tracex-toggle"
              checked={isEnabled}
              onCheckedChange={handleToggle}
            />
          </FlexRow>
        </FlexRow>

        {isEnabled && (
          <div className="border-t border-border p-4">
            <FlexCol gap="2">
              <FlexRow alignItems="center" gap="2" className="h-5">
                <Label htmlFor="tracex-api-token">API Token</Label>
                {saveStatus === 'saving' && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="size-3 animate-spin" />
                    Saving…
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle2 className="size-3" />
                    Saved
                  </span>
                )}
              </FlexRow>
              <Input
                id="tracex-api-token"
                type="text"
                value={apiTokenInput}
                onChange={e => {
                  setApiTokenInput(e.target.value);
                  setHasEdited(true);
                  setSaveStatus('idle');
                }}
                placeholder="Enter your TraceX API token"
              />
            </FlexCol>
          </div>
        )}
      </div>
    </FlexCol>
  );
};
